import React, { useEffect, useState } from 'react';
import { Plus, Search, Wrench, X } from 'lucide-react';
import { maintenanceAPI } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import toast from 'react-hot-toast';
import { format } from 'date-fns';

const PRIORITY_STYLES = {
  urgent: { color: '#DC2626', bg: '#FEF2F2' },
  high: { color: '#EA580C', bg: '#FFF7ED' },
  medium: { color: '#D97706', bg: '#FFFBEB' },
  low: { color: '#6B7280', bg: '#F3F4F6' },
};
const STATUS_STYLES = {
  open: { color: '#DC2626', bg: '#FEF2F2' },
  assigned: { color: '#2563EB', bg: '#EFF6FF' },
  in_progress: { color: '#D97706', bg: '#FFFBEB' },
  pending_parts: { color: '#7C3AED', bg: '#F5F3FF' },
  completed: { color: '#059669', bg: '#ECFDF5' },
  closed: { color: '#6B7280', bg: '#F3F4F6' },
  cancelled: { color: '#9CA3AF', bg: '#F9FAFB' },
};

const MaintenancePage = () => {
  const { canManage, user } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [pagination, setPagination] = useState({ total: 0, page: 1, pages: 1 });

  const [form, setForm] = useState({
    title: '', description: '', category: 'other', priority: 'medium',
    propertyId: '', estimatedCost: ''
  });

  const fetchTickets = async (page = 1) => {
    setLoading(true);
    try {
      const params = { page, limit: 20 };
      if (statusFilter) params.status = statusFilter;
      if (priorityFilter) params.priority = priorityFilter;
      const res = await maintenanceAPI.getAll(params);
      setTickets(res.data.data || []);
      setPagination(res.data.pagination || {});
    } catch {
      toast.error('Failed to load tickets');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchTickets(); }, [statusFilter, priorityFilter]);

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await maintenanceAPI.create(form);
      toast.success('Ticket created');
      setShowForm(false);
      setForm({ title: '', description: '', category: 'other', priority: 'medium', propertyId: '', estimatedCost: '' });
      fetchTickets();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to create ticket');
    }
  };

  const handleStatusUpdate = async (id, status) => {
    try {
      await maintenanceAPI.update(id, { status });
      toast.success('Status updated');
      fetchTickets();
      if (selectedTicket?._id === id) {
        const res = await maintenanceAPI.getOne(id);
        setSelectedTicket(res.data.data);
      }
    } catch {
      toast.error('Update failed');
    }
  };

  const filtered = tickets.filter(t => {
    if (!search) return true;
    const q = search.toLowerCase();
    return t.title?.toLowerCase().includes(q) || t.ticketNumber?.toLowerCase().includes(q) || t.property?.title?.toLowerCase().includes(q);
  });

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Maintenance</h1>
          <p className="page-subtitle">{pagination.total} total tickets</p>
        </div>
        <button className="btn btn-primary" onClick={() => setShowForm(true)}>
          <Plus size={16} /> New Ticket
        </button>
      </div>

      <div className="filters-bar">
        <div className="search-bar">
          <Search size={16} />
          <input placeholder="Search tickets..." value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <select className="form-select" style={{ width: 160 }} value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
          <option value="">All Status</option>
          {Object.keys(STATUS_STYLES).map(s => <option key={s} value={s}>{s.replace('_', ' ').replace(/\b\w/g, c => c.toUpperCase())}</option>)}
        </select>
        <select className="form-select" style={{ width: 140 }} value={priorityFilter} onChange={e => setPriorityFilter(e.target.value)}>
          <option value="">All Priority</option>
          {['urgent', 'high', 'medium', 'low'].map(p => <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>)}
        </select>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: selectedTicket ? '1fr 400px' : '1fr', gap: 20 }}>
        {/* Ticket List */}
        <div className="card" style={{ padding: 0 }}>
          <div className="table-container">
            <table className="table">
              <thead>
                <tr><th>Ticket #</th><th>Title</th><th>Property</th><th>Category</th><th>Priority</th><th>Status</th><th>Reported</th><th>Actions</th></tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={8} style={{ textAlign: 'center', padding: 40 }}><div className="spinner" style={{ margin: '0 auto' }} /></td></tr>
                ) : filtered.length === 0 ? (
                  <tr><td colSpan={8}><div className="empty-state"><Wrench size={40} /><h3>No tickets found</h3></div></td></tr>
                ) : filtered.map(t => (
                  <tr key={t._id} style={{ cursor: 'pointer', background: selectedTicket?._id === t._id ? '#F0F9FF' : 'transparent' }}
                    onClick={() => setSelectedTicket(selectedTicket?._id === t._id ? null : t)}>
                    <td>
                      <span style={{ fontFamily: 'monospace', fontSize: 12, background: '#F3F4F6', padding: '2px 8px', borderRadius: 4 }}>
                        {t.ticketNumber}
                      </span>
                    </td>
                    <td>
                      <div style={{ fontWeight: 500, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.title}</div>
                      {t.guestImpact && <span className="badge badge-red" style={{ fontSize: 10, padding: '1px 6px' }}>Guest Impact</span>}
                    </td>
                    <td style={{ fontSize: 13, color: '#6B7280' }}>{t.property?.title || t.equipment?.name || '—'}</td>
                    <td><span style={{ fontSize: 13, textTransform: 'capitalize' }}>{t.category?.replace('_', ' ')}</span></td>
                    <td><span className="badge" style={PRIORITY_STYLES[t.priority]}>{t.priority}</span></td>
                    <td><span className="badge" style={STATUS_STYLES[t.status]}>{t.status?.replace('_', ' ')}</span></td>
                    <td style={{ fontSize: 13 }}>{t.reportedBy?.firstName} {t.reportedBy?.lastName}</td>
                    <td>
                      <select className="form-select" style={{ padding: '4px 8px', fontSize: 12, width: 130 }}
                        value={t.status} onClick={e => e.stopPropagation()}
                        onChange={e => { e.stopPropagation(); handleStatusUpdate(t._id, e.target.value); }}>
                        {Object.keys(STATUS_STYLES).map(s => <option key={s} value={s}>{s.replace('_', ' ').replace(/\b\w/g, c => c.toUpperCase())}</option>)}
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Ticket Detail Panel */}
        {selectedTicket && (
          <div className="card" style={{ position: 'sticky', top: 88, maxHeight: 'calc(100vh - 120px)', overflowY: 'auto' }}>
            <div className="card-header">
              <span className="card-title">#{selectedTicket.ticketNumber}</span>
              <button className="btn btn-ghost btn-icon btn-sm" onClick={() => setSelectedTicket(null)}><X size={16} /></button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div>
                <div style={{ fontWeight: 600, fontSize: 15, marginBottom: 6 }}>{selectedTicket.title}</div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <span className="badge" style={PRIORITY_STYLES[selectedTicket.priority]}>{selectedTicket.priority}</span>
                  <span className="badge" style={STATUS_STYLES[selectedTicket.status]}>{selectedTicket.status?.replace('_', ' ')}</span>
                  {selectedTicket.guestImpact && <span className="badge badge-red">Guest Impact</span>}
                </div>
              </div>
              {selectedTicket.description && (
                <div>
                  <div style={{ fontSize: 12, color: '#9CA3AF', marginBottom: 4, textTransform: 'uppercase' }}>Description</div>
                  <p style={{ fontSize: 14, color: '#374151', lineHeight: 1.6 }}>{selectedTicket.description}</p>
                </div>
              )}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {[
                  ['Category', selectedTicket.category?.replace('_', ' ')],
                  ['Property', selectedTicket.property?.title || '—'],
                  ['Reported By', `${selectedTicket.reportedBy?.firstName || ''} ${selectedTicket.reportedBy?.lastName || ''}`],
                  ['Assigned To', selectedTicket.assignedTo ? `${selectedTicket.assignedTo.firstName} ${selectedTicket.assignedTo.lastName}` : 'Unassigned'],
                  ['Est. Cost', selectedTicket.estimatedCost ? `$${selectedTicket.estimatedCost}` : '—'],
                  ['Created', selectedTicket.createdAt ? format(new Date(selectedTicket.createdAt), 'MMM d, yyyy') : '—'],
                ].map(([k, v]) => (
                  <div key={k} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                    <span style={{ color: '#9CA3AF' }}>{k}</span>
                    <span style={{ fontWeight: 500, textTransform: 'capitalize', textAlign: 'right', maxWidth: 200 }}>{v}</span>
                  </div>
                ))}
              </div>
              {/* Notes */}
              {(selectedTicket.notes || []).length > 0 && (
                <div>
                  <div style={{ fontSize: 12, color: '#9CA3AF', marginBottom: 8, textTransform: 'uppercase' }}>Notes</div>
                  {selectedTicket.notes.map((n, i) => (
                    <div key={i} style={{ padding: '8px 10px', background: '#F9FAFB', borderRadius: 6, marginBottom: 6 }}>
                      <div style={{ fontSize: 13, color: '#374151' }}>{n.text}</div>
                      <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 4 }}>
                        {n.addedBy?.firstName} · {n.addedAt ? format(new Date(n.addedAt), 'MMM d') : ''}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Create Ticket Modal */}
      {showForm && (
        <div className="modal-overlay" onClick={() => setShowForm(false)}>
          <div className="modal modal-md" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <span className="modal-title">New Maintenance Ticket</span>
              <button className="btn btn-ghost btn-icon btn-sm" onClick={() => setShowForm(false)}><X size={16} /></button>
            </div>
            <form onSubmit={handleCreate}>
              <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                <div className="form-group">
                  <label className="form-label">Title *</label>
                  <input className="form-input" placeholder="e.g. Leaking faucet in bathroom" value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))} required />
                </div>
                <div className="form-group">
                  <label className="form-label">Description</label>
                  <textarea className="form-textarea" rows={3} placeholder="Describe the issue in detail..." value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} />
                </div>
                <div className="form-grid form-grid-2">
                  <div className="form-group">
                    <label className="form-label">Category</label>
                    <select className="form-select" value={form.category} onChange={e => setForm(p => ({ ...p, category: e.target.value }))}>
                      {['plumbing', 'electrical', 'hvac', 'appliance', 'structural', 'cleaning', 'landscaping', 'security', 'equipment_repair', 'other'].map(c => (
                        <option key={c} value={c}>{c.replace('_', ' ').replace(/\b\w/g, x => x.toUpperCase())}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Priority</label>
                    <select className="form-select" value={form.priority} onChange={e => setForm(p => ({ ...p, priority: e.target.value }))}>
                      {['low', 'medium', 'high', 'urgent'].map(p => <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Estimated Cost ($)</label>
                    <input type="number" className="form-input" min={0} placeholder="0" value={form.estimatedCost} onChange={e => setForm(p => ({ ...p, estimatedCost: e.target.value }))} />
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowForm(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary">Create Ticket</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default MaintenancePage;
